import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useBranch, useBranchAPI, useFilterParams } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Alert, ButtonGroup, Tab, Nav, Dropdown, Collapse } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { ReportCardComponent } from '../../components/cards';
import { Database, Person, Cpu, Building, Trophy, Award } from 'react-bootstrap-icons';
import {
  AlertCircle,
  TrendingDown,
  TrendingUp,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Wallet,
} from 'lucide-react';
import { reportAPI, dashboardAPI } from '../../services';
import { clearTokenFromLocalStorage } from '../../utils/auth';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import RevenueSection from '../../components/dashboard/RevenueSection';
import CashierSection from '../../components/dashboard/CashierSection';
import StaffMonitoringSection from '../../components/dashboard/StaffMonitoringSection';
import DateRangePicker from '../../components/fields/DateRangePicker';
import KPICard from '../../components/cards/KPICard';
import PendingReceiptsLine from '../../components/dashboard/PendingReceiptsLine';
import CashBalanceHistoryModal from '../../components/dashboard/CashBalanceHistoryModal';
import { usePendingApprovals } from '../../context/PendingApprovals';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

const DASHBOARD_FILTER_SPEC = {
  from: { default: () => moment().format('YYYY-MM-DD'), valid: isIsoDate },
  to: { default: () => moment().format('YYYY-MM-DD'), valid: isIsoDate },
};

export default function HomePage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  // #021749 — modal lịch sử biến động Tiền Két, mở từ ô "Tiền Két Hiện Tại".
  const [showCashHistory, setShowCashHistory] = useState(false);
  const { withBranch } = useBranchAPI();
  // #231533 — số việc đang chờ Admin duyệt, cùng nguồn với badge trên sidebar.
  const { supplierOrderChanges: pendingSupplierChanges } = usePendingApprovals();

  const [stats] = useState({
    totalEmployees: 0,
    totalShifts: 0,
    activeShifts: 0,
  });
  const [loading] = useState(false);
  const [error] = useState(null);
  const [dateRange, setDateRange] = useState('7days');
  const [reports, setReports] = useState({
    topCompletedDifficultShifts: [],
    topRewardPoints: [],
    topDeductedPoints: [],
    topLeastRegisteredShifts: [],
    employeesByRewardRatio: [],
  });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState('difficult-shifts');

  // Dashboard tracking states
  const [showStaffSection, setShowStaffSection] = useState(true);
  const [showRevenueSection, setShowRevenueSection] = useState(true);
  const [showCashierSection, setShowCashierSection] = useState(true);
  /**
   * #191439 — Màn này (route `/home`) là ĐÍCH THẬT của link sâu trong 3 tin doanh thu (dòng
   * 24, 25, 33 của bảng khảo sát #132320): bất thường doanh thu theo giờ, tổng hợp bất thường
   * trong ngày, báo cáo doanh thu ngày.
   *
   * Bảng khảo sát đề nghị `/analytics`, nhưng đã mở `src/pages/main/AnalyticsPage.js` ra đọc:
   * đó là TRANG MẪU của bộ giao diện, vẽ từ 3 tệp JSON tĩnh
   * (`assets/data/analytics.json`, `traffics.json`, `pages.json`) — không có doanh thu, không
   * có chi nhánh, không có ngày. Gửi sếp vào đó là gửi vào dữ liệu giả, tệ hơn là không có
   * link. Khối doanh thu THẬT (`reportAPI.getRevenueReport`) nằm ở đây.
   *
   * `from`/`to` là bộ tên chuẩn ở `KHUON-CHUAN-URL-PARAM.md` mục 1.
   */
  const [dashboardUrlFilters, setDashboardUrlFilters, , { ignored: dashboardIgnored, dismissIgnored: dismissDashboardIgnored }] =
    useFilterParams(DASHBOARD_FILTER_SPEC);
  const dashboardFromDate = dashboardUrlFilters.from;
  const dashboardToDate = dashboardUrlFilters.to;
  const [revenueData, setRevenueData] = useState(null);
  const [revenueGroupBy, setRevenueGroupBy] = useState('DAY'); // DAY | WEEK | MONTH
  const [cashierData, setCashierData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [cashierLoading, setCashierLoading] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  // Staff monitoring date range states
  const [staffFromDate, setStaffFromDate] = useState(moment().format('YYYY-MM-DD'));
  const [staffToDate, setStaffToDate] = useState(moment().format('YYYY-MM-DD'));

  // Staff monitoring filter + pagination (server-side)
  const [staffSearch, setStaffSearch] = useState('');
  const [staffStatus, setStaffStatus] = useState('WORKING');
  const [staffPage, setStaffPage] = useState(1);
  const STAFF_PAGE_SIZE = 12;

  // Đổi bộ lọc / khoảng ngày / chi nhánh → quay về trang đầu
  useEffect(() => {
    setStaffPage(1);
  }, [staffSearch, staffStatus, staffFromDate, staffToDate, selectedBranch?.id]);

  // Tham số lọc + phân trang gửi lên /shift-tasks/monitor-employees
  const staffMonitorFilters = useMemo(
    () => ({
      page: staffPage,
      limit: STAFF_PAGE_SIZE,
      ...(staffSearch.trim() ? { search: staffSearch.trim() } : {}),
      ...(staffStatus !== 'ALL' ? { status: staffStatus } : {}),
    }),
    [staffPage, staffSearch, staffStatus],
  );

  const getDateRange = useCallback((range) => {
    const vietnamTimezone = 'Asia/Ho_Chi_Minh';
    const endDate = moment.tz(vietnamTimezone).endOf('day');
    const startDate = moment.tz(vietnamTimezone).startOf('day');

    switch (range) {
      case 'day':
        break;
      case '7days':
        startDate.subtract(7, 'days');
        break;
      case '15days':
        startDate.subtract(15, 'days');
        break;
      case '30days':
        startDate.subtract(30, 'days');
        break;
      default:
        startDate.subtract(7, 'days');
    }

    return {
      from_date: startDate.format('YYYY-MM-DD'),
      to_date: endDate.format('YYYY-MM-DD'),
    };
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setReportsLoading(true);
      const dateParams = getDateRange(dateRange);

      const [
        topCompletedDifficultShifts,
        topRewardPoints,
        topDeductedPoints,
        topLeastRegisteredShifts,
        employeesByRewardRatio,
      ] = await Promise.all([
        reportAPI.getTopCompletedDifficultShifts({ ...dateParams, limit: 10 }),
        reportAPI.getTopRewardPoints({ ...dateParams, limit: 10 }),
        reportAPI.getTopDeductedPoints({ ...dateParams, limit: 10 }),
        reportAPI.getTopLeastRegisteredShifts({ ...dateParams, limit: 10 }),
        reportAPI.getEmployeesByRewardRatio({ ...dateParams, limit: 20 }),
      ]);

      setReports({
        topCompletedDifficultShifts: topCompletedDifficultShifts || [],
        topRewardPoints: topRewardPoints || [],
        topDeductedPoints: topDeductedPoints || [],
        topLeastRegisteredShifts: topLeastRegisteredShifts || [],
        employeesByRewardRatio: employeesByRewardRatio || [],
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
      if (error?.status === 401 || error?.status === 403) {
        clearTokenFromLocalStorage();
        window.location.href = '/login';
      } else {
        toast.error('Lỗi khi tải dữ liệu báo cáo');
      }
    } finally {
      setReportsLoading(false);
    }
  }, [dateRange, getDateRange]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {

    try {
      const params = {
        from_date: dashboardFromDate,
        to_date: dashboardToDate,
      };

      if (selectedBranch?.id) {
        params.branch_id = selectedBranch.id;
      }

      // Fetch all dashboard data in parallel
      const [revenue, cashier, staff] = await Promise.all([
        dashboardAPI
          .getRevenueReport({ ...params, group_by: revenueGroupBy })
          .catch(() => null),
        dashboardAPI.getCashierReport(params).catch(() => null),
        dashboardAPI
          .monitorEmployees({
            branch_id: selectedBranch?.id,
            date_from: dashboardFromDate,
            date_to: dashboardToDate,
            ...staffMonitorFilters,
          })
          .catch(() => null),
      ]);

      setRevenueData(revenue);
      setCashierData(cashier);
      setStaffData(staff);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error toast for initial load failures
    }
  }, [
    dashboardFromDate,
    dashboardToDate,
    selectedBranch,
    revenueGroupBy,
    staffMonitorFilters,
  ]);

  // Fetch revenue data
  const fetchRevenueData = useCallback(async () => {

    try {
      setRevenueLoading(true);
      const params = {
        from_date: dashboardFromDate,
        to_date: dashboardToDate,
        group_by: revenueGroupBy,
      };

      if (selectedBranch?.id) {
        params.branch_id = selectedBranch.id;
      }

      const data = await dashboardAPI.getRevenueReport(params);
      setRevenueData(data);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      if (error?.status !== 404) {
        toast.error('Lỗi khi tải dữ liệu doanh thu');
      }
    } finally {
      setRevenueLoading(false);
    }
  }, [dashboardFromDate, dashboardToDate, selectedBranch, revenueGroupBy]);

  // Fetch staff data
  const fetchStaffData = useCallback(async () => {

    try {
      setStaffLoading(true);

      let params = {};
      if (staffFromDate && staffToDate) {
        params.date_from = staffFromDate;
        params.date_to = staffToDate;
      } else {
        params.date = moment().format('YYYY-MM-DD');
      }

      if (selectedBranch?.id) {
        params.branch_id = selectedBranch.id;
      }

      const data = await dashboardAPI.monitorEmployees({
        ...params,
        ...staffMonitorFilters,
      });
      setStaffData(data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      if (error?.status !== 404) {
        toast.error('Lỗi khi tải dữ liệu nhân viên');
      }
    } finally {
      setStaffLoading(false);
    }
  }, [selectedBranch, staffFromDate, staffToDate, staffMonitorFilters]);

  // Handle view shift detail
  const handleViewShift = (shiftId) => {
    navigate(`/cashier-reconciliation/${shiftId}`);
  };

  // Load reports khi dateRange thay đổi
  useEffect(() => {
    fetchReports();
    const interval = setInterval(fetchReports, 300000); // Cập nhật sau mỗi 5 phút (cache TTL)
    return () => clearInterval(interval);
  }, [fetchReports]);

  // Load dashboard data when branch or date range changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto refresh staff data every 60 seconds (only for today).
  // #223445 — công tắc "Auto refresh" đã gỡ cùng khối tiêu đề; tự làm mới LUÔN bật như mặc định cũ.
  useEffect(() => {
    // Only auto refresh if viewing today's data
    const isToday = staffFromDate === moment().format('YYYY-MM-DD') && staffToDate === moment().format('YYYY-MM-DD');

    if (!isToday) return;

    const interval = setInterval(() => {
      if (showStaffSection) {
        fetchStaffData(); // Only refresh staff data for real-time monitoring
      }
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [showStaffSection, staffFromDate, staffToDate, fetchStaffData]);

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <h1 className="mc-page-title">Trang chủ</h1>
          <p className="mc-page-description">Quản lý hệ thống HRM Coffee Chain</p>
        </div>

        {/* #175019 — khối "Chọn chi nhánh" của Dashboard (đã comment từ trước) xoá hẳn:
            chi nhánh chỉ chọn ở thanh trên đầu. */}

        {/* #231533 — Ô "CẦN DUYỆT" đặt NGAY DƯỚI tiêu đề, trước mọi biểu đồ.
            Điều tra #223612: NCC gửi yêu cầu sửa đơn xong thì tưởng đã xong và ngồi chờ, còn màn
            duyệt lại nằm ở mục thứ 4/5 trong submenu "Nhập hàng & Nhà cung cấp" — không ai mở thì
            yêu cầu nằm đó mãi. Chỉ hiện khi CÓ việc chờ, để trang chủ không mọc thêm ô rỗng. */}
        {pendingSupplierChanges > 0 && (
          <div className="mc-page-section">
            <Alert
              variant="warning"
              className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-0"
            >
              <div className="d-flex align-items-center gap-2">
                <AlertCircle size={20} />
                <span>
                  <strong>Cần duyệt:</strong> {pendingSupplierChanges} yêu cầu sửa đơn từ Nhà cung cấp đang chờ.
                </span>
              </div>
              <Button
                variant="warning"
                size="sm"
                onClick={() => navigate('/inventory/supplier-order-changes')}
              >
                Xem &amp; duyệt ngay
              </Button>
            </Alert>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mc-page-section">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {/* Dashboard Tracking Section
            #223445 (góp ý Trợ năng f2325c48, "bỏ cái này đi") — gỡ hẳn khối tiêu đề của thẻ: chữ
            "Dashboard Tracking", giờ "Cập nhật", công tắc "Auto refresh", nút "Làm mới" và nút bật/tắt
            bộ lọc. Bộ lọc ngày vốn mở sẵn (mặc định cũ showFilters=true) nên nay hiện cố định;
            tự làm mới 60s vẫn chạy như mặc định cũ (autoRefresh=true). */}
        <div className="mc-page-section">
            <Card className="mc-card">
              <Card.Body>
                {/* Dashboard Filters */}
                <div>
                    <InvalidParamsNotice
                      ignored={dashboardIgnored}
                      onDismiss={dismissDashboardIgnored}
                    />
                    <DateRangePicker
                      fromDate={dashboardFromDate}
                      toDate={dashboardToDate}
                      defaultPreset="today"
                      compact
                      onChange={(from, to) => {
                        // Một lần ghi URL cho cả 2 mốc — gọi tách 2 lần sẽ ghi URL 2 lượt và
                        // lượt sau đọc lại giá trị cũ của lượt trước.
                        setDashboardUrlFilters({ from, to });
                        setStaffFromDate(from);
                        setStaffToDate(to);
                      }}
                    />
                    <hr className="my-4" />
                </div>

                {/* Tiền Két Section */}
                <div className="mb-5">
                  <Row>
                    <Col xs={12} sm={6} md={4}>
                      {/* #021749 — bấm vào ô để xem lịch sử biến động (sổ quỹ). */}
                      <KPICard
                        title="💵 Tiền Két Hiện Tại"
                        value={cashierData?.summary?.current_cash_balance || 0}
                        icon={Wallet}
                        loading={cashierLoading}
                        color="success"
                        suffix="đ"
                        onClick={() => setShowCashHistory(true)}
                        clickHint="Bấm để xem lịch sử biến động"
                      />
                    </Col>
                  </Row>

                  {/* #182201 (lệnh sếp) — thay thẻ 5 dòng "Phiếu nhập hàng" (#145125) bằng ĐÚNG
                      1 dòng "N đơn hàng đang chờ nhập kho", bấm vào mở bảng danh sách — cùng khuôn
                      với dòng "NVL" trên thẻ Tổng chi phí. Nguồn số đổi sang luồng đặt hàng NCC
                      thật (PurchaseOrder → GoodsReceipt) thay vì phiếu nhập thủ công NK- vốn gần
                      như không ai dùng (điều tra #175223). */}
                  <div className="mt-2">
                    <PendingReceiptsLine
                      selectedBranch={selectedBranch}
                    />
                  </div>

                  {/* Bấm ô "Tiền Két Hiện Tại" ở trên mở modal LỊCH SỬ biến động Tiền Két
                      (#021749). #175410 (góp ý Spot Fix 3f65c9fe) gỡ khối "Tổng hợp Két theo kỳ"
                      khỏi modal, nên không còn truyền kỳ from/toDate xuống nữa. */}
                  <CashBalanceHistoryModal
                    show={showCashHistory}
                    onHide={() => setShowCashHistory(false)}
                    branchId={selectedBranch?.id}
                    branchName={selectedBranch?.name}
                  />
                </div>

                {/* Revenue Section */}
                <div className="mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">📊 Doanh Thu</h5>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={() => setShowRevenueSection(!showRevenueSection)}
                    >
                      {showRevenueSection ? <Eye size={20} /> : <EyeOff size={20} />}
                    </Button>
                  </div>
                  <Collapse in={showRevenueSection}>
                    <div>
                      <RevenueSection
                        data={revenueData}
                        loading={revenueLoading}
                        selectedBranch={selectedBranch}
                        onReload={fetchRevenueData}
                        groupBy={revenueGroupBy}
                        onGroupByChange={setRevenueGroupBy}
                      />
                    </div>
                  </Collapse>
                </div>

                <hr className="my-4" />

                {/* Staff Monitoring Section */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">👥 Theo Dõi Nhân Viên</h5>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={() => setShowStaffSection(!showStaffSection)}
                    >
                      {showStaffSection ? <Eye size={20} /> : <EyeOff size={20} />}
                    </Button>
                  </div>
                  <Collapse in={showStaffSection}>
                    <div>
                      <StaffMonitoringSection
                        data={staffData}
                        loading={staffLoading}
                        dateFrom={staffFromDate}
                        dateTo={staffToDate}
                        onDateFromChange={setStaffFromDate}
                        onDateToChange={setStaffToDate}
                        onTaskAssigned={fetchStaffData}
                        search={staffSearch}
                        onSearchChange={setStaffSearch}
                        status={staffStatus}
                        onStatusChange={setStaffStatus}
                        page={staffPage}
                        onPageChange={setStaffPage}
                      />
                    </div>
                  </Collapse>
                </div>
                <hr className="my-4" />

                {/* Cashier Section */}
                <div className="mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">💰 Quản Lý Tiền Mặt</h5>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={() => setShowCashierSection(!showCashierSection)}
                    >
                      {showCashierSection ? <Eye size={20} /> : <EyeOff size={20} />}
                    </Button>
                  </div>
                  <Collapse in={showCashierSection}>
                    <div>
                      <CashierSection
                        data={cashierData}
                        loading={cashierLoading}
                        onViewShift={handleViewShift}
                      />
                    </div>
                  </Collapse>
                </div>
              </Card.Body>
            </Card>
          </div>

        {/* Báo Cáo Dashboard */}
        <div className="mc-page-section">
          <Card className="mc-card">
            <Card.Header>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h4 className="mc-card-title mb-0">Báo Cáo Nhân Viên</h4>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <label className="form-label mb-0">Khoảng thời gian:</label>
                  <ButtonGroup size="sm">
                    {[
                      { range: 'day', text: '1 Ngày' },
                      { range: '7days', text: '7 Ngày' },
                      { range: '15days', text: '15 Ngày' },
                      { range: '30days', text: '30 Ngày' },
                    ].map(({ range, text }) => (
                      <Button
                        key={range}
                        variant={dateRange === range ? 'primary' : 'outline-primary'}
                        className="px-2 text-nowrap"
                        onClick={() => setDateRange(range)}
                      >
                        {text}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Tab.Container
                activeKey={activeReportTab}
                onSelect={setActiveReportTab}
              >
                <Nav
                  variant="tabs"
                  className="mb-3"
                >
                  <Nav.Item>
                    <Nav.Link
                      eventKey="difficult-shifts"
                      className="d-flex align-items-center gap-2"
                    >
                      <Trophy size={16} />
                      Top Ca Khó
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="reward-points"
                      className="d-flex align-items-center gap-2"
                    >
                      <Award size={16} />
                      Top Điểm Thưởng
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="deducted-points"
                      className="d-flex align-items-center gap-2"
                    >
                      <AlertCircle size={16} />
                      Top Điểm Bị Trừ
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="least-shifts"
                      className="d-flex align-items-center gap-2"
                    >
                      <TrendingDown size={16} />
                      Ít Đăng Ký Ca
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="reward-ratio"
                      className="d-flex align-items-center gap-2"
                    >
                      <TrendingUp size={16} />
                      Tỉ Lệ Phần Thưởng
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="difficult-shifts">
                    <ReportCardComponent
                      title="Top Hoàn Thành Ca Khó"
                      data={reports.topCompletedDifficultShifts}
                      valueKey="count"
                      icon="trophy"
                      variant="success"
                      loading={reportsLoading}
                    />
                  </Tab.Pane>

                  <Tab.Pane eventKey="reward-points">
                    <ReportCardComponent
                      title="Top Điểm Thưởng"
                      data={reports.topRewardPoints}
                      valueKey="points"
                      icon="award"
                      variant="primary"
                      loading={reportsLoading}
                    />
                  </Tab.Pane>

                  <Tab.Pane eventKey="deducted-points">
                    <ReportCardComponent
                      title="Top Điểm Bị Trừ"
                      data={reports.topDeductedPoints}
                      valueKey="points"
                      icon="alert"
                      variant="danger"
                      loading={reportsLoading}
                    />
                  </Tab.Pane>

                  <Tab.Pane eventKey="least-shifts">
                    <ReportCardComponent
                      title="Top Ít Đăng Ký Ca"
                      data={reports.topLeastRegisteredShifts}
                      valueKey="count"
                      icon="trending-down"
                      variant="warning"
                      loading={reportsLoading}
                    />
                  </Tab.Pane>

                  <Tab.Pane eventKey="reward-ratio">
                    <ReportCardComponent
                      title="Nhân Viên Theo Tỉ Lệ Phần Thưởng"
                      data={reports.employeesByRewardRatio}
                      valueKey="total_reward_rate_bonus"
                      icon="trending-up"
                      variant="info"
                      loading={reportsLoading}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
